<script setup lang="ts">
import { ut_isPrint } from '@base/utils/utils'
import { ut_wilo_pictureSize } from '~/utils/utils'

const p = defineProps<{
  src: string
  alt: string
  clickMax?: boolean
  width?: number
  height?: number
  maxWidth?: number
  maxHeight?: number
  minWidth?: number
  minHeight?: number
  modalFixSize?: boolean
  isOld?: boolean
  noLazy?: boolean
  wiloSize?: number
  wiloSizePrint?: number
  wiloSizeMax?: number
  customClass?: string
  footer?: string
  showMagnifyIcon?: boolean
  onlyMagnifyIcon?: boolean
}>()

const modalOpen: Ref<boolean> = ref(false)
const isAltPic: boolean = p.src === useAppConfig().altPic

/* if (p.noLazy) {
  useHead({
    link: [
      { href: p.src, rel: 'preload', as: 'image', fetchpriority: 'high' },
    ],
  })
} */

/*
 * ⚑ `width` carries `!important`, the others do not — `main.css` holds the Adesso overwrite
 * `#__nuxt img { width: auto !important }`, and nothing but `!important` beats it from a style
 * attribute. A width that a caller states explicitly has to win over that blanket tag rule; the
 * two callers that state one are the print sizes of the comparison photo (`Compare.vue`) and of
 * the curve (`PumpCurve.vue`), and both were silently ignored — the photo grew to the cell width,
 * and the curve, sitting in a `flex`/`mx-auto` wrapper where `width:auto` has nothing to resolve
 * against, collapsed to 0 and vanished from every austauschspiegel pdf.
 *
 * `height`, `max-*` and `min-*` stay plain: no rule fights them, and an `!important` that nothing
 * needs would only be harder to override later.
 */
function getSize(): string {
  let res: string = ''
  if (p.width) { res += 'width:' + p.width + 'px !important;' }
  if (p.height) { res += 'height:' + p.height + 'px;' }
  if (p.maxWidth) { res += 'max-width:' + p.maxWidth + 'px;' }
  if (p.maxHeight) { res += 'max-height:' + p.maxHeight + 'px;' }
  if (p.minWidth) { res += 'min-width:' + p.minWidth + 'px;' }
  if (p.minHeight) { res += 'min-height:' + p.minHeight + 'px;' }
  return res
}

function getSrc(): string {
  return ((p.wiloSize || p.wiloSizePrint) && !isAltPic) ? ut_wilo_pictureSize(p.src, ut_isPrint() ? p.wiloSizePrint ? p.wiloSizePrint : 5 : p.wiloSize || 5) : p.src
}

function getSrcMax(): string {
  return p.wiloSize ? ut_wilo_pictureSize(p.src, p.wiloSizeMax ? p.wiloSizeMax : 5) : p.src
}

function getSrcSet(): string {
  // 150 270 560 860 1024
  // ToDo activate HiRes option?
  if (p.wiloSize && !isAltPic) {
    return ut_wilo_pictureSize(p.src, p.wiloSize) + ' 1x,'
    // + (p.wiloSize < 5 ? ut_wilo_pictureSize(p.src, p.wiloSize + 1, false) + ' 2x,' : '')
    // + (p.wiloSize < 4 ? ut_wilo_pictureSize(p.src, p.wiloSize + 2, false) + ' 3x,' : '')
  }
  else {
    return p.src + ' 1x,'
  }
}
</script>

<template>
  <img
    v-if="!onlyMagnifyIcon"
    :src="getSrc()"
    :srcset="!ut_isPrint() ? getSrcSet() : ''"
    :alt="alt"
    :loading="ut_isPrint() || noLazy ? 'eager' : 'lazy'"
    :fetchpriority="noLazy ? 'high' : 'auto'"
    :style="getSize()"
    :class="[isOld ? 'grayscale' : '', (clickMax && !isAltPic) ? 'cursor-pointer' : '', customClass ? customClass : '']"
    class="break-inside-avoid"
    @click="(clickMax && !isAltPic) ? modalOpen = true : ''"
  >
  <div v-if="footer || showMagnifyIcon" class="pt-4 flex items-center" :class="clickMax ? 'cursor-pointer' : ''" @click="(clickMax) ? modalOpen = true : ''">
    <UIcon v-if="showMagnifyIcon" name="i-wilo-magnifying-glass-solid-full" color="tertiary" class="size-5.5 mr-3" />
    <span v-html="footer" />
  </div>

  <UModal
    v-if="clickMax && !ut_isPrint()"
    v-model:open="modalOpen"
    :title="alt"
    :ui="useAppConfig().modalFrame.auto"
  >
    <template #body>
      <img
        :src="getSrcMax()"
        :alt="alt"
        loading="lazy"
        :style="modalFixSize ? 'height:75vh;width:100vb' : ''"
        :class="isOld ? 'grayscale' : ''"
      >
    </template>
  </UModal>
</template>
